import '../assets/index'
import { AppContextProvider } from '../hooks/useAppContext'
import { Outlet, Route, Routes } from 'react-router-dom'
import type { ReactNode } from 'react'
import React from 'react'
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { routesDef } from '../config/routes'
import { LayoutWithApolloProvider } from '../components/LayoutWithApolloProvider'

import { Page as ProductListPage } from './search/list/index.page'
import { Page as ProductItemPage } from './search/item/index.page'
import { Page as HomePage } from './home/home.page'
import { Page as PostItemPage } from './posts/item/index.page'
import { Page as PostListPage } from './posts/list/index.page'
import { Page as UseAndCareItemPage } from './use-and-care/item/index.page'
import { Page as UseAndCareListPage } from './use-and-care/list/index.page'
import { Page as Login } from './account/login/index.page'
import { Page as LoginSuccess } from './account/login-success/index.page'
import { Page as LoginFailed } from './account/login-failed/index.page'
import { Page as UpdateRegistration } from './account/update-registration/index.page'
import { Page as Join } from './account/join/index.page'
import { Page as RegisterPage } from './account/register/index.page'
import { Page as ChangePasswordPage } from './account/change-password/index.page'
import { Page as AccountOverviewPage } from './profile/overview/index.page'
import { Page as AccountOrdersPage } from './profile/orders/list/index.page'
import { Page as AccountOrderItemPage } from './profile/orders/item/index.page'
import { Page as AccountMessagesPage } from './profile/messages/list/index.page'
import { Page as AccountMessagePage } from './profile/messages/detail/index.page'
import { Page as AccountBoardsPage } from './profile/boards/list/index.page'
import { Page as AccountBoardItemPage } from './profile/boards/item/index.page'
import { Page as AccountSettingItemPage } from './profile/settings/index.page'
import { Page as CartPage } from './cart/index.page'
import InvoicePage from './invoice'
import { InvoiceViewPage } from './invoice-view'
import { InvoiceLinkExpired } from './invoice/InvoiceLinkExpired'
import CheckoutSuccessPage from './checkout/success'
import CheckoutCancelPage from './checkout/cancel'
import CheckoutMessageSentPage from './checkout/MessageSent'
import { NotFound } from './NotFound'

export type AppProps = { apolloClient: ApolloClient<NormalizedCacheObject>; children?: ReactNode }
export default function App(props: AppProps) {
  return (
    <AppContextProvider>
      <Routes>
        <Route element={<LayoutWithApolloProvider apolloClient={props.apolloClient} />}>
          {/*The bare layout with header and footer, not margins in the middle*/}
          <Route path={routesDef.HomePage.path} element={<HomePage />} />
          <Route path={routesDef.ProductListPage.path} element={<ProductListPage />} />
          <Route path={routesDef.ProductItemPage.path} element={<ProductItemPage />} />

          <Route
            element={
              <div className="pt-3 pb-5">
                <Outlet />
              </div>
            }
          >
            {/*The layout with margins in the middle content */}
            <Route path={routesDef.LoginPage.path} element={<Login />} />
            <Route path={routesDef.JoinPage.path} element={<Join />} />
            <Route path={routesDef.UpdateUserRegistration.path} element={<UpdateRegistration />} />
            <Route path={routesDef.AuthSuccessPage.path} element={<LoginSuccess />} />
            <Route path={routesDef.AuthFailedPage.path} element={<LoginFailed />} />
            <Route path={routesDef.RegisterPage.path} element={<RegisterPage />} />
            <Route path={routesDef.ChangePasswordPage.path} element={<ChangePasswordPage />} />

            {/*Use and Care*/}
            <Route path={routesDef.UseAndCareListPage.path} element={<UseAndCareListPage />} />
            <Route path={routesDef.UseAndCareItemPage.path} element={<UseAndCareItemPage />} />

            {/*Posts*/}
            <Route path={routesDef.PostItemPage.path} element={<PostItemPage />} />
            <Route path={routesDef.PostListPage.path} element={<PostListPage />} />

            {/*Account */}
            <Route path={routesDef.AccountOverview.path} element={<AccountOverviewPage />} />
            <Route path={routesDef.AccountOrders.path} element={<AccountOrdersPage />} />
            <Route path={routesDef.AccountOrderItem.path} element={<AccountOrderItemPage />} />
            <Route path={routesDef.AccountMessages.path} element={<AccountMessagesPage />} />
            <Route path={routesDef.AccountMessageDetails.path} element={<AccountMessagePage />} />
            <Route path={routesDef.AccountBoardsPage.path} element={<AccountBoardsPage />} />
            <Route path={routesDef.AccountBoardsDetailsPage.path} element={<AccountBoardItemPage />} />
            <Route path={routesDef.AccountSettings.path} element={<AccountSettingItemPage />} />

            {/*Cart Page page*/}
            <Route path={routesDef.CartPage.path} element={<CartPage />} />

            {/*checkout success and cancel page*/}
            <Route path={routesDef.CheckoutSuccess.path} element={<CheckoutSuccessPage />} />
            <Route path={routesDef.CheckoutCancel.path} element={<CheckoutCancelPage />} />
            <Route path={routesDef.CheckoutMessageSent.path} element={<CheckoutMessageSentPage />} />
          </Route>
        </Route>

        {/*The layout without the footer or */}
        <Route path={routesDef.InvoicePage.path} element={<InvoicePage />} />
        <Route path={routesDef.InvoiceViewPage.path} element={<InvoiceViewPage />} />
        <Route path={routesDef.InvoiceViewExpiredPage.path} element={<InvoiceLinkExpired />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppContextProvider>
  )
}
